(() => {
  const DEFAULT_DATA_URL = 'https://wpc-wildlife-live.jbmanning5.workers.dev/dashboard.json';
  const PUBLIC_AUDIO_BASE_URL = 'https://pub-ead7f6590ab5448e9c1ed42230f2bd83.r2.dev/';
  const SOURCES = ['hearingNow', 'recentlyHeard', 'recentDetections', 'latestDetections', 'detections', 'todayDetections', 'todaySpecies'];
  const escapeHtml = value => String(value ?? '').replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;' })[char]);
  const isUrl = value => /^https?:\/\//i.test(String(value || '').trim());
  const lookupKey = value => String(value || '').trim().toLowerCase();
  const validImage = value => typeof value === 'string' && (value.startsWith('data:image/') || isUrl(value));

  function resolveAudioUrl(value) {
    const trimmed = String(value || '').trim();
    return !trimmed ? '' : isUrl(trimmed) ? trimmed : `${PUBLIC_AUDIO_BASE_URL}${trimmed.replace(/^\/+/, '')}`;
  }

  function detectionTime(item) {
    const value = item.lastHeardAt || item.lastHeard || item.last_heard || item.last_heard_at || item.detectedAt || item.detected_at || item.timestamp || item.time || item.date;
    const parsed = value ? new Date(value).getTime() : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function normalize(item, images) {
    const commonName = item.commonName || item.common_name || item.species || item.name || 'Unknown species';
    const scientificName = item.scientificName || item.scientific_name || '';
    const image = item.thumbnail_url || item.thumbnailUrl || item.imageUrl || item.image || item.thumbnailDataUri || item.thumbnail_data_uri || images.scientific.get(lookupKey(scientificName)) || images.common.get(lookupKey(commonName)) || '';
    return { commonName, scientificName, image: validImage(image) ? image : '', clipUrl: resolveAudioUrl(item.clipUrl || item.clip_url || item.audioUrl || item.audio_url || item.audio || ''), time: detectionTime(item) || Number(item.time || 0), timeLabel: item.lastHeardLabel || item.status || '', confidence: item.confidence || item.confidenceLabel || item.score || '' };
  }

  function collectImages(value, images) {
    if (Array.isArray(value)) return value.forEach(item => collectImages(item, images));
    if (!value || typeof value !== 'object') return;
    const image = value.thumbnailDataUri || value.thumbnail_data_uri || value.thumbnailUrl || value.thumbnail_url || value.imageUrl || '';
    if (validImage(image)) {
      const scientific = lookupKey(value.scientificName || value.scientific_name);
      const common = lookupKey(value.commonName || value.common_name || value.species || value.name);
      if (scientific && !images.scientific.has(scientific)) images.scientific.set(scientific, image);
      if (common && !images.common.has(common)) images.common.set(common, image);
    }
    Object.values(value).forEach(item => { if (item && typeof item === 'object') collectImages(item, images); });
  }

  function recentItems(data, cache) {
    const images = { scientific: new Map(), common: new Map() };
    collectImages(data, images);
    const candidates = SOURCES.flatMap(key => Array.isArray(data[key]) ? data[key] : []).concat(cache);
    const seen = new Set();
    return candidates.map(item => normalize(item, images)).sort((a, b) => b.time - a.time).filter(item => {
      const key = lookupKey(item.commonName || item.scientificName);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 6);
  }

  function readCache(key) { try { const value = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(value) ? value : []; } catch (_) { return []; } }
  function writeCache(key, items) { try { localStorage.setItem(key, JSON.stringify(items.filter(item => item.clipUrl).slice(0, 6))); } catch (_) {} }
  function label(item, prefix) { return item.time ? `${prefix} at ${new Date(item.time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : item.timeLabel || `${prefix} recently`; }
  function confidence(item) { const value = item.confidence; if (value === '' || value === null || value === undefined) return ''; const number = Number(value); return Number.isFinite(number) && number >= 0 && number <= 1 ? `${Math.round(number * 100)}% confidence` : `Confidence: ${value}`; }

  function setButton(button, state, name) {
    button.classList.toggle('is-loading', state === 'loading');
    button.classList.toggle('is-playing', state === 'playing');
    button.textContent = state === 'loading' ? '…' : state === 'playing' ? '❚❚' : state === 'error' ? '!' : '▶';
    button.setAttribute('aria-pressed', String(state === 'playing'));
    button.setAttribute('aria-label', `${state === 'playing' ? 'Pause' : state === 'loading' ? 'Loading' : 'Play'} ${name} audio`);
  }

  function stop(root) {
    const state = root._wildlifeRecentlyHeard;
    if (!state?.audio) return;
    const { audio, button, name } = state;
    state.audio = null; state.button = null; state.url = '';
    audio.pause(); audio.removeAttribute('src'); audio.load();
    if (button) setButton(button, 'idle', name);
  }

  function bind(root) {
    root.addEventListener('click', event => {
      const button = event.target.closest('[data-wrh-audio-url]');
      if (!button || !root.contains(button)) return;
      event.preventDefault();
      const state = root._wildlifeRecentlyHeard;
      const url = button.dataset.wrhAudioUrl;
      const name = button.dataset.wrhAudioName || 'wildlife';
      if (!url) return;
      if (state.audio && state.button === button && state.url === url) { if (state.audio.paused) state.audio.play().catch(() => setButton(button, 'error', name)); else state.audio.pause(); return; }
      stop(root);
      const audio = new Audio(url);
      state.audio = audio; state.button = button; state.url = url; state.name = name;
      setButton(button, 'loading', name);
      audio.addEventListener('playing', () => { if (state.audio === audio) setButton(button, 'playing', name); });
      audio.addEventListener('pause', () => { if (state.audio === audio && !audio.ended) setButton(button, 'idle', name); });
      audio.addEventListener('ended', () => { if (state.audio === audio) stop(root); });
      audio.addEventListener('error', () => { if (state.audio === audio) { stop(root); setButton(button, 'error', name); window.setTimeout(() => setButton(button, 'idle', name), 1200); } });
      audio.play().catch(() => { if (state.audio === audio) { stop(root); setButton(button, 'error', name); window.setTimeout(() => setButton(button, 'idle', name), 1200); } });
    });
  }

  function render(root, items, delayed) {
    const list = root.querySelector('[data-wrh-list]'); const status = root.querySelector('[data-wrh-status]');
    status.className = 'wildlife-recently-heard__status';
    status.textContent = delayed ? 'Showing saved detections' : items.length ? 'Updated recently' : 'Quiet right now';
    if (delayed) status.classList.add('is-delayed');
    if (!items.length) { list.innerHTML = '<p class="wildlife-recently-heard__message">The woods are quiet right now. Check back soon.</p>'; return; }
    list.innerHTML = items.map(item => `<article class="wildlife-recently-heard__card"><div class="wildlife-recently-heard__thumb">${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.commonName)}" loading="lazy">` : '♪'}</div><div class="wildlife-recently-heard__copy"><strong class="wildlife-recently-heard__name">${escapeHtml(item.commonName)}</strong>${item.scientificName ? `<em class="wildlife-recently-heard__scientific">${escapeHtml(item.scientificName)}</em>` : ''}<div class="wildlife-recently-heard__details"><span class="wildlife-recently-heard__meta">${escapeHtml(label(item, delayed ? 'Last received' : 'Heard'))}</span>${confidence(item) ? `<span class="wildlife-recently-heard__meta">${escapeHtml(confidence(item))}</span>` : ''}</div></div>${item.clipUrl ? `<button class="wildlife-recently-heard__play" type="button" data-wrh-audio-url="${escapeHtml(item.clipUrl)}" data-wrh-audio-name="${escapeHtml(item.commonName)}" aria-label="Play ${escapeHtml(item.commonName)} audio" aria-pressed="false">▶</button>` : ''}</article>`).join('');
  }

  async function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true'; root._wildlifeRecentlyHeard = { audio: null, button: null, url: '', name: '' }; bind(root);
    const cacheKey = root.dataset.cacheKey || 'wildlife-recently-heard'; const cached = readCache(cacheKey); const url = root.dataset.dataUrl || DEFAULT_DATA_URL;
    try { const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`, { cache: 'no-store' }); if (!response.ok) throw new Error(String(response.status)); const data = await response.json(); const items = recentItems(data, cached); writeCache(cacheKey, items); render(root, items, false); }
    catch (_) { render(root, recentItems({}, cached), true); const status = root.querySelector('[data-wrh-status]'); if (!cached.length) { status.textContent = 'Data unavailable'; status.classList.remove('is-delayed'); status.classList.add('is-offline'); } }
  }

  function start(scope = document) { scope.querySelectorAll?.('[data-wildlife-recently-heard]').forEach(init); }
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => start()) : start();
  document.addEventListener('shopify:section:load', event => start(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll?.('[data-wildlife-recently-heard]').forEach(stop));
})();
